/**
 * Admin Trades Page
 *
 * Displays list of all trades with status filters, address search,
 * sortable columns, and pagination.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAdminTrades,
  getAdminTradeStats,
} from '../services/adminApi.js';
import type {
  AdminTradeListItem,
  AdminTradesListResponse,
  AdminTradeStats,
  AdminTradeStatus,
} from '../types/admin.js';
import { AdminLayout } from '../components/AdminLayout.js';
import { ErrorDisplay } from '../components/admin/ErrorDisplay.js';
import { TradeStatusBadge } from '../components/admin/TradeStatusBadge.js';

// ==================== Helpers ====================

function formatXrp(drops: string | number | null | undefined): string {
  if (drops === null || drops === undefined || drops === '0') return '-';
  return `${(Number(drops) / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} XRP`;
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function truncateId(id: string): string {
  return `${id.slice(0, 8)}...`;
}

function truncateAddress(address: string): string {
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

function getXrpSweetener(trade: AdminTradeListItem): string {
  const initiator = Number(trade.initiator_xrp_drops);
  const recipient = Number(trade.recipient_xrp_drops);
  if (initiator > 0) return formatXrp(trade.initiator_xrp_drops);
  if (recipient > 0) return formatXrp(trade.recipient_xrp_drops);
  return '-';
}

// ==================== Types ====================

type SortField = 'created_at' | 'updated_at' | 'expires_at' | 'status';

interface StatusTab {
  key: string;
  label: string;
  statuses: string;
}

const STATUS_TABS: StatusTab[] = [
  { key: 'all', label: 'All', statuses: '' },
  { key: 'active', label: 'Active', statuses: 'proposed,countered,accepted,signing,settling' },
  { key: 'completed', label: 'Completed', statuses: 'completed' },
  { key: 'failed', label: 'Failed', statuses: 'declined,expired,cancelled' },
];

// ==================== Sort Header Component ====================

function SortHeader({
  label,
  field,
  currentSort,
  currentOrder,
  onSort,
}: {
  label: string;
  field: SortField;
  currentSort: SortField;
  currentOrder: 'asc' | 'desc';
  onSort: (field: SortField) => void;
}) {
  const isActive = currentSort === field;
  return (
    <th
      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 cursor-pointer hover:text-gray-700 select-none"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {isActive && (
          <span className="text-blue-600">
            {currentOrder === 'asc' ? '\u2191' : '\u2193'}
          </span>
        )}
      </div>
    </th>
  );
}

// ==================== Main Component ====================

export function AdminTradesPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<AdminTradesListResponse | null>(null);
  const [stats, setStats] = useState<AdminTradeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('all');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [addressInput, setAddressInput] = useState('');
  const [addressFilter, setAddressFilter] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const tab = STATUS_TABS.find((t) => t.key === activeTab);
      const statusParam = tab?.statuses || undefined;

      const result = await getAdminTrades({
        page,
        limit: 50,
        status: statusParam || undefined,
        address: addressFilter || undefined,
        sort: sortField,
        order: sortOrder,
      });
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load trades');
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, sortField, sortOrder, addressFilter]);

  const loadStats = useCallback(async () => {
    try {
      const result = await getAdminTradeStats();
      setStats(result);
    } catch {
      // Stats failure is non-critical
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleTabChange = (tabKey: string) => {
    setActiveTab(tabKey);
    setPage(1);
  };

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const handleAddressSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setAddressFilter(addressInput.trim());
    setPage(1);
  };

  const handleClearAddress = () => {
    setAddressInput('');
    setAddressFilter('');
    setPage(1);
  };

  const handleCopyId = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const trades = data?.trades || [];
  const statusCounts = data?.statusCounts || {};
  const pagination = data?.pagination;

  // Calculate tab counts
  const getTabCount = (tab: StatusTab): number => {
    if (tab.key === 'all') {
      return Object.values(statusCounts).reduce((a, b) => a + b, 0);
    }
    const statuses = tab.statuses.split(',');
    return statuses.reduce((sum, s) => sum + (statusCounts[s] || 0), 0);
  };

  return (
    <AdminLayout>
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Trades</h1>
            <p className="mt-2 text-sm text-gray-600">
              Monitor peer-to-peer NFT trades
            </p>
          </div>
          <button
            onClick={() => { loadData(); loadStats(); }}
            disabled={loading}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="mb-6 grid gap-4 sm:grid-cols-5">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 shadow-sm">
              <div className="text-sm font-medium text-blue-600">Active</div>
              <div className="mt-1 text-2xl font-bold text-blue-700">{stats.totalActive}</div>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 shadow-sm">
              <div className="text-sm font-medium text-green-600">Completed Today</div>
              <div className="mt-1 text-2xl font-bold text-green-700">{stats.completedToday}</div>
            </div>
            <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 shadow-sm">
              <div className="text-sm font-medium text-purple-600">This Week</div>
              <div className="mt-1 text-2xl font-bold text-purple-700">{stats.completedThisWeek}</div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="text-sm font-medium text-gray-500">XRP Volume</div>
              <div className="mt-1 text-2xl font-bold text-gray-900">
                {stats.totalXrpVolume.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <div className="text-sm font-medium text-gray-500">Avg Rounds</div>
              <div className="mt-1 text-2xl font-bold text-gray-900">{stats.avgNegotiationRounds}</div>
            </div>
          </div>
        )}

        {/* Filter Row: Tabs + Address Search */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 pb-4">
          <div className="flex flex-wrap gap-2">
            {STATUS_TABS.map((tab) => {
              const count = getTabCount(tab);
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {tab.label}
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs ${
                      isActive ? 'bg-blue-200' : 'bg-gray-200'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <form onSubmit={handleAddressSearch} className="flex items-center gap-2">
            <input
              type="text"
              value={addressInput}
              onChange={(e) => setAddressInput(e.target.value)}
              placeholder="Filter by wallet address..."
              className="w-64 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              type="submit"
              className="rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Search
            </button>
            {addressFilter && (
              <button
                type="button"
                onClick={handleClearAddress}
                className="rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-200"
              >
                Clear
              </button>
            )}
          </form>
        </div>

        {/* Error State */}
        {error && (
          <ErrorDisplay error={error} onRetry={loadData} className="mb-6" />
        )}

        {/* Loading State */}
        {loading && trades.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
              <p className="mt-4 text-sm text-gray-600">Loading trades...</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && trades.length === 0 && (
          <div className="rounded-lg border border-gray-200 bg-white p-12 text-center">
            <p className="text-gray-500">
              {addressFilter
                ? `No trades found for address ${truncateAddress(addressFilter)}.`
                : activeTab === 'all'
                  ? 'No trades found.'
                  : `No ${activeTab} trades found.`}
            </p>
          </div>
        )}

        {/* Trades Table */}
        {trades.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Trade ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Initiator
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Recipient
                    </th>
                    <SortHeader label="Status" field="status" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} />
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      Counters
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      NFTs
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      XRP
                    </th>
                    <SortHeader label="Expires" field="expires_at" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} />
                    <SortHeader label="Created" field="created_at" currentSort={sortField} currentOrder={sortOrder} onSort={handleSort} />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {trades.map((trade) => (
                    <tr
                      key={trade.id}
                      onClick={() => navigate(`/admin/trades/${trade.id}`)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-mono text-gray-600">
                            {truncateId(trade.id)}
                          </span>
                          <button
                            onClick={(e) => handleCopyId(trade.id, e)}
                            className="text-gray-400 hover:text-gray-600"
                            title="Copy trade ID"
                          >
                            {copiedId === trade.id ? (
                              <svg className="h-3.5 w-3.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                            ) : (
                              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {trade.initiator_username || truncateAddress(trade.initiator_address)}
                        </div>
                        {trade.initiator_username && (
                          <div className="text-xs text-gray-500 font-mono">
                            {truncateAddress(trade.initiator_address)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {trade.recipient_username || truncateAddress(trade.recipient_address)}
                        </div>
                        {trade.recipient_username && (
                          <div className="text-xs text-gray-500 font-mono">
                            {truncateAddress(trade.recipient_address)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <TradeStatusBadge status={trade.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {trade.current_version - 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {trade.initiator_nft_count} / {trade.recipient_nft_count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {getXrpSweetener(trade)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(trade.expires_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(trade.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-6 py-3">
                <div className="text-sm text-gray-700">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  {pagination.total} trades
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(1)}
                    disabled={pagination.page <= 1}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    First
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={pagination.page <= 1}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                    disabled={pagination.page >= pagination.totalPages}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                  <button
                    onClick={() => setPage(pagination.totalPages)}
                    disabled={pagination.page >= pagination.totalPages}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Last
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
